import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Bell,
  CheckCircle2,
  AlertTriangle,
  Info,
  XCircle,
  CheckCheck,
} from "lucide-react";
import { useHive } from "@/store/useHive";

const TONE: Record<string, { icon: any; color: string }> = {
  success: { icon: CheckCircle2, color: "#22c55e" },
  warning: { icon: AlertTriangle, color: "#f59e0b" },
  error: { icon: XCircle, color: "#f43f5e" },
  info: { icon: Info, color: "#3b82f6" },
};

export function NotificationCenter() {
  const { notifOpen, setNotif, notifications, markAllRead, dismissNotif } = useHive();
  const unread = notifications.filter((n) => !n.read).length;

  return (
    <AnimatePresence>
      {notifOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[80]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setNotif(false)}
          />
          <motion.div
            initial={{ x: 380, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 380, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="fixed right-3 top-[60px] z-[90] flex max-h-[calc(100vh-160px)] w-[360px] flex-col overflow-hidden rounded-2xl border border-app-strong glass-strong shadow-window"
          >
            <div className="flex items-center justify-between border-b border-app px-4 py-3">
              <div className="flex items-center gap-2">
                <Bell size={16} className="accent" />
                <span className="text-sm font-semibold text-app">Notifications</span>
                {unread > 0 && (
                  <span className="rounded-full bg-rose-500/15 px-1.5 text-[10px] font-medium text-rose-500">
                    {unread} new
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] text-app-2 transition hover:bg-surface-2"
                >
                  <CheckCheck size={13} /> Read all
                </button>
                <button onClick={() => setNotif(false)} className="rounded-lg p-1 text-app-2 transition hover:bg-surface-2">
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {notifications.length === 0 && (
                <div className="py-16 text-center text-sm text-app-2">All caught up 🎉</div>
              )}
              <AnimatePresence initial={false}>
                {notifications.map((n) => {
                  const t = TONE[n.tone];
                  return (
                    <motion.div
                      key={n.id}
                      layout
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: 40, height: 0 }}
                      className="group relative mb-1.5 flex gap-3 rounded-xl border border-app bg-surface-2/40 p-3"
                    >
                      {!n.read && (
                        <span className="absolute left-1 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full bg-accent" />
                      )}
                      <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg" style={{ background: `${t.color}1f` }}>
                        <t.icon size={16} style={{ color: t.color }} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-app">{n.title}</span>
                          <span className="text-[10px] text-app-2">{n.time}</span>
                        </div>
                        <p className="mt-0.5 text-xs leading-relaxed text-app-2">{n.body}</p>
                      </div>
                      <button
                        onClick={() => dismissNotif(n.id)}
                        className="absolute right-1.5 top-1.5 rounded p-0.5 text-app-2 opacity-0 transition hover:bg-surface-2 group-hover:opacity-100"
                      >
                        <X size={13} />
                      </button>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
